// adminProductExport.js — the pure half of /admin/product's export (#223).
//
// Spec: docs/specs/2026-08-24-admin-product-export-design.md.
//
// Everything here is a function of its arguments: no store, no $t, no DOM, no
// network. ProductTables.vue keeps only the parts that cannot be pure — the
// lazy WMS fetch, SheetJS's writeFile, the Blob anchor, and the swal dialogs.
// That split is what makes the rules below testable in a repo that cannot mount
// a .vue under jest (reference-bo-fn-jest-infra).
//
// THE LABELS IN THIS FILE ARE HARDCODED THAI ON PURPOSE (spec §3.3). They are
// file CONTENT, not UI: routing them through $t would make the same export
// produce different files for a th user and an en user, and break every
// downstream consumer the moment somebody flips the language toggle.

import Fuse from "fuse.js";

// 32,767 is Excel's hard ceiling for one cell. 32,000 leaves room for the
// quoting SheetJS adds and for a reader who widens the JSON by hand.
export const JSON_CELL_BUDGET = 32000;

// Same two strings the table itself renders (ProductTables.vue:119-120, :315).
export const STATUS_LABELS = { 1: "ใช้งาน", 2: "ไม่ใช้งาน" };

// Same three the product form's dropdown renders (ProductForm.vue:141-143 →
// th.js visibility_*). The int is 0/1/2 (BO-Product.go:297).
export const VISIBILITY_LABELS = {
	0: "แสดงทั้งหมด",
	1: "เฉพาะบริษัท",
	2: "เฉพาะตัวแทน",
};

// Marks a `via` path as "drawn at random from a pool", so a reader looking at
// wms_products can tell a guaranteed pick from a possible one.
export const RANDOM_VIA_SUFFIX = " (สุ่ม)";

export const statusLabel = (status) => STATUS_LABELS[Number(status)] || "";

export const visibilityLabel = (visibility) => {
	const n = Number(visibility);
	return Object.prototype.hasOwnProperty.call(VISIBILITY_LABELS, n)
		? VISIBILITY_LABELS[n]
		: "";
};

// Go's zero time marshals as "0001-01-01 00:00:00" and MariaDB's as
// "0000-00-00 00:00:00" (LocalTime.MarshalJSON, controllers/util.go:19). Both
// mean "no date". Same test as ProductForm.vue:558-562 — kept as a string
// compare rather than a Date parse because "0000-…" is not a parseable date.
export const dateOrNull = (value) => {
	if (value === null || value === undefined || value === "") return null;
	const s = String(value);
	if (s.startsWith("0001") || s.startsWith("0000")) return null;
	return s;
};

export const numberOrNull = (value) => {
	if (value === null || value === undefined || value === "") return null;
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
};

// buildWmsIndex turns the WMS product list into id → name.
//
// NOTE THE CAPITALISATION: WMS rows are ID/Name, not id/name
// (ProductForm.vue:277, :623-632). Reading `row.name` here is the single most
// likely way to get a file full of nulls that still "works".
export const buildWmsIndex = (wmsRows) => {
	const index = {};
	(Array.isArray(wmsRows) ? wmsRows : []).forEach((row) => {
		if (!row) return;
		const id = Number(row.ID !== undefined ? row.ID : row.id);
		if (!Number.isFinite(id) || id <= 0) return;
		const name = row.Name !== undefined ? row.Name : row.name;
		if (name === null || name === undefined || name === "") return;
		index[id] = String(name);
	});
	return index;
};

const wmsName = (wmsIndex, id, fallback) => {
	const idx = wmsIndex || {};
	const n = Number(id);
	if (Object.prototype.hasOwnProperty.call(idx, n)) return idx[n];
	// A stored name (random pools carry one — ValidateProductRandom fills them
	// server-side, BO-ProductStruct.go:109-116) beats nothing. Direct maps do not:
	// wms_product_name never survives a save (BO-ProductStruct.go:169-174 has no
	// such field), which is why the index exists at all.
	if (fallback !== null && fallback !== undefined && fallback !== "") {
		return String(fallback);
	}
	// null, never "" — "the lookup did not answer" is a different fact from
	// "the WMS product is called the empty string", and §2.2 makes a failed
	// lookup a non-fatal, visible outcome.
	return null;
};

const randomPoolId = (entry) => {
	const r = entry && entry.wms_product_random;
	if (!r) return 0;
	const id = Number(r.bo_product_random_id);
	return Number.isFinite(id) && id > 0 ? id : 0;
};

const randomPoolProducts = (entry, wmsIndex) => {
	const r = (entry && entry.wms_product_random) || {};
	// `wms_product`, NOT `products` — the Go field is Products but the tag is
	// json:"wms_product,omitempty" (BO-ProductStruct.go:343). Writing the Go
	// name here reads undefined and silently empties every pool.
	const list = Array.isArray(r.wms_product) ? r.wms_product : [];
	return list
		.map((p) => {
			const id = Number(p && p.wms_product_id);
			if (!Number.isFinite(id) || id <= 0) return null;
			return { wms_product_id: id, name: wmsName(wmsIndex, id, p && p.name) };
		})
		.filter(Boolean);
};

// parseAttributes reads bo_product.attributes and reshapes it for a reader.
//
// Input is the raw JSON STRING off the list response (DBProduct.Attributes,
// BO-ProductStruct.go:23) — already-parsed arrays are accepted too so a caller
// that has the tree in hand does not have to re-stringify it.
//
// Tolerant on purpose, exactly like ProductForm.vue:489-502: unparseable → [],
// null → [], and a bare `{}` (what an empty attributes column round-trips to)
// → []. An export must not be the thing that throws on one bad row.
//
// Output per option, and the two rules that matter:
//
//   { variable, options: [ { name, picks: [{wms_product_id, wms_product_name,
//                                           quantity}] } ] }
//
//   * `wms_product_random` is DROPPED when bo_product_random_id is 0. Go's
//     omitempty is a no-op on structs, so every plain direct map carries a
//     phantom {"bo_product_random_id":0} (§1.5a). Passing it through would show
//     every reader a random pool that does not exist.
//   * when it is non-zero the option is a random-pool option: the pool is
//     lifted to a sibling `random_pool` and `picks` is OMITTED entirely. Such an
//     option has no fixed pick — the server even overwrites wms_product_id with
//     an arbitrary pool member (BO-ProductStruct.go:335) — so an empty `picks`
//     array next to it would invite exactly the wrong reading.
export const parseAttributes = (raw, wmsIndex) => {
	let tree = raw;
	if (typeof raw === "string") {
		if (raw.trim() === "") return [];
		try {
			tree = JSON.parse(raw);
		} catch (e) {
			return [];
		}
	}
	if (!Array.isArray(tree)) return [];

	return tree
		.map((attr) => {
			if (!attr) return null;
			const options = (Array.isArray(attr.options) ? attr.options : [])
				.map((option) => {
					if (!option) return null;
					const entries = Array.isArray(option.wms_map) ? option.wms_map : [];
					const out = { name: option.name === undefined ? "" : String(option.name) };

					const pool = entries.find((e) => randomPoolId(e) > 0);
					if (pool) {
						out.random_pool = {
							id: randomPoolId(pool),
							products: randomPoolProducts(pool, wmsIndex),
						};
						return out;
					}

					out.picks = entries
						.map((e) => {
							const id = Number(e && e.wms_product_id);
							if (!Number.isFinite(id) || id <= 0) return null;
							const qty = Number(e.pickup_quantity);
							return {
								wms_product_id: id,
								wms_product_name: wmsName(wmsIndex, id, null),
								quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
							};
						})
						.filter(Boolean);
					return out;
				})
				.filter(Boolean);

			return {
				variable: attr.variable === undefined ? "" : String(attr.variable),
				options,
			};
		})
		.filter(Boolean);
};

// countOptions — the OptionCount column: every option across every variable.
export const countOptions = (variables) =>
	(Array.isArray(variables) ? variables : []).reduce(
		(sum, v) => sum + ((v && Array.isArray(v.options) ? v.options.length : 0)),
		0
	);

// variablesText — the Variables column. One variable per line, options joined
// with ", ": "สี: แดง, น้ำเงิน". For eyeballing in a spreadsheet; the JSON
// columns are the machine-readable view.
export const variablesText = (variables) =>
	(Array.isArray(variables) ? variables : [])
		.map((v) => {
			const names = (v && Array.isArray(v.options) ? v.options : [])
				.map((o) => (o && o.name) || "")
				.filter((n) => n !== "");
			return `${(v && v.variable) || ""}: ${names.join(", ")}`;
		})
		.join("\n");

// flattenWmsProducts — the reconciliation view: every WMS product this product
// can reach, once each, with every "variable / option" path that reaches it.
//
// It walks pool members as well as direct picks, so nothing is lost even for an
// option whose extra pools parseAttributes collapsed.
export const flattenWmsProducts = (variables) => {
	const byId = new Map();
	const add = (id, name, via) => {
		if (!Number.isFinite(id) || id <= 0) return;
		if (!byId.has(id)) {
			byId.set(id, { wms_product_id: id, wms_product_name: name, via: [] });
		}
		const entry = byId.get(id);
		// A name from one path fills a null left by another; the first non-null
		// wins so the result does not depend on option order.
		if (entry.wms_product_name === null && name !== null && name !== undefined) {
			entry.wms_product_name = name;
		}
		if (!entry.via.includes(via)) entry.via.push(via);
	};

	(Array.isArray(variables) ? variables : []).forEach((v) => {
		const varName = (v && v.variable) || "";
		(v && Array.isArray(v.options) ? v.options : []).forEach((o) => {
			const path = `${varName} / ${(o && o.name) || ""}`;
			if (o && o.random_pool) {
				(o.random_pool.products || []).forEach((p) =>
					add(p.wms_product_id, p.name, path + RANDOM_VIA_SUFFIX)
				);
				return;
			}
			(o && Array.isArray(o.picks) ? o.picks : []).forEach((p) =>
				add(p.wms_product_id, p.wms_product_name, path)
			);
		});
	});

	return Array.from(byId.values());
};

// jsonCell renders one value for one Excel cell under the 32,000-char budget.
//
// Over budget, the WHOLE cell becomes a self-describing marker — never a
// truncated string. A JSON string cut mid-value is invalid JSON: a downstream
// reader either crashes on it or, worse, parses a prefix and believes it. That
// is strictly worse than an honest absence, and the row's *Count column still
// carries the true size (§3.2).
//
// `items` is what the marker reports as the size of the thing it dropped. The
// caller passes the count that means something to a reader (options, WMS
// products); with no third argument it falls back to the value's own top-level
// length, because a marker saying items:0 would read like "there was nothing
// there" — the opposite of why the marker exists.
export const jsonCell = (value, budget = JSON_CELL_BUDGET, items = null) => {
	const limit = Number.isFinite(Number(budget)) ? Number(budget) : JSON_CELL_BUDGET;
	const text = JSON.stringify(value === undefined ? null : value);
	const chars = text.length;
	// `items == null` on purpose, not Number.isFinite(Number(items)):
	// Number(null) is 0, which is finite, so the finite test would accept the
	// default and report items:0 on every omitted cell.
	let count = items === null || items === undefined ? NaN : Number(items);
	if (!Number.isFinite(count)) {
		if (Array.isArray(value)) count = value.length;
		else if (value && typeof value === "object") count = Object.keys(value).length;
		else count = 0;
	}
	if (chars <= limit) {
		return { text, omitted: false, chars, items: count };
	}
	return {
		text: JSON.stringify({
			_omitted: true,
			reason: "exceeds_excel_cell_limit",
			chars,
			items: count,
			use: "JSON export",
		}),
		omitted: true,
		chars,
		items: count,
	};
};

// exportRows builds the xlsx rows.
//
// The six original columns keep their names, order and meaning (NO, Name, Code,
// Price, Tag, Status — ProductTables.vue:308-318); the five new ones follow
// Status. Returns the omitted product codes alongside, because the one warning
// §3.2 asks for has to name them and only this pass knows which.
export const exportRows = (products, wmsIndex, budget = JSON_CELL_BUDGET) => {
	const rows = [];
	const omittedCodes = [];
	let no = 1;

	(Array.isArray(products) ? products : []).forEach((product) => {
		if (!product) return;
		const variables = parseAttributes(product.attributes, wmsIndex);
		const wmsProducts = flattenWmsProducts(variables);
		const optionCount = countOptions(variables);

		const optionsCell = jsonCell(variables, budget, optionCount);
		const wmsCell = jsonCell(wmsProducts, budget, wmsProducts.length);
		if (optionsCell.omitted || wmsCell.omitted) {
			const code = product.code || product.name || "";
			if (code !== "" && !omittedCodes.includes(code)) omittedCodes.push(code);
		}

		rows.push({
			NO: no++,
			Name: product.name,
			Code: product.code,
			Price: product.price,
			Tag: product.tag,
			Status: statusLabel(product.status),
			Variables: variablesText(variables),
			OptionCount: optionCount,
			OptionsJSON: optionsCell.text,
			// The counts are the cheap insurance that makes the omit rule legible:
			// a reader still sees the true size of a cell that was dropped.
			WmsProductCount: wmsProducts.length,
			WmsProductsJSON: wmsCell.text,
		});
	});

	return { rows, omittedCodes };
};

// jsonProduct — one product in the pure-JSON mode's shape (§3.3).
//
// Dropped, and why (§3.3's table): `id` and `enterprise_id` are internal PKs a
// reader cannot use — `code` is the identity, and it is unique on create
// (BO-Product.go:313-319); the four `platform_*` fields are constant because
// this page hard-filters platform_id 1 (ProductTables.vue:256-260); raw
// `attributes` is superseded by `variables` and keeping both invites them to
// disagree; `attributes_st` / `price_tiers_st` / `products_sku` are never
// populated by this endpoint (BO-Product.go:120-139); `deleted_at` because the
// list is live rows only; `created_at` because `updated_at` is the freshness
// signal that gets used.
//
// `detail` and `bo_product_category_id` are not in §3.3's shape either, so they
// stay out: detail is a description blob that would dominate the file.
//
// `shipping_cost` was in §3.3's shape and emitted as a permanent null, with the
// reasoning that a visible null is how the next reader learns the field is not
// there. #299 settled the question the null was holding open: `bo_product` has no
// such column, DBProduct (BO-ProductStruct.go:10-45) has no field for one, and
// there is no product-level shipping cost anywhere in the backend to wire it to —
// every shipping_cost in mdf-bo-api is on an order. So the key is gone, together
// with the ค่าขนส่ง column it mirrored on ProductTables.vue. A null that will
// never be filled is not a signpost, it is a column every consumer has to decide
// to ignore.
export const jsonProduct = (product, wmsIndex, options = {}) => {
	const imageUrl = typeof options.imageUrl === "function" ? options.imageUrl : () => null;
	const variables = parseAttributes(product.attributes, wmsIndex);
	let priceTiers = [];
	if (Array.isArray(product.price_tiers)) {
		priceTiers = product.price_tiers;
	} else if (typeof product.price_tiers === "string" && product.price_tiers.trim() !== "") {
		try {
			const parsed = JSON.parse(product.price_tiers);
			if (Array.isArray(parsed)) priceTiers = parsed;
		} catch (e) {
			priceTiers = [];
		}
	}

	return {
		code: product.code === undefined ? null : product.code,
		name: product.name === undefined ? null : product.name,
		price: numberOrNull(product.price),
		tag: product.tag === undefined || product.tag === null ? "" : product.tag,
		status: statusLabel(product.status),
		image_url: product.images ? imageUrl(product.images) : null,
		updated_at: dateOrNull(product.updated_at),
		sale_start: dateOrNull(product.sale_start),
		sale_end: dateOrNull(product.sale_end),
		flags: {
			visibility: visibilityLabel(product.visibility),
			is_comboset: Number(product.is_comboset) === 1,
			cod: Number(product.cod) === 1,
			stock_sync: Number(product.stock_sync_enabled) === 1,
		},
		app: {
			enabled: Number(product.app_enable) === 1,
			tag: product.app_tag || "",
			discount: numberOrNull(product.app_discount),
			discount_start: dateOrNull(product.app_discount_start),
			discount_end: dateOrNull(product.app_discount_end),
		},
		price_tiers: priceTiers
			.map((t) => ({ min_qty: numberOrNull(t && t.min_qty), price: numberOrNull(t && t.price) }))
			.filter((t) => t.min_qty !== null || t.price !== null),
		variables,
		wms_products: flattenWmsProducts(variables),
	};
};

// jsonPayload — the whole pure-JSON file. One object, one `products` array,
// plus the filter that produced it so a reader knows what they are holding.
// `exportedAt` is injected rather than read from the clock so the shape is
// testable.
export const jsonPayload = (products, wmsIndex, options = {}) => {
	const list = (Array.isArray(products) ? products : []).filter(Boolean);
	return {
		exported_at: options.exportedAt || null,
		filter: options.filter || {},
		product_count: list.length,
		products: list.map((p) => jsonProduct(p, wmsIndex, options)),
	};
};

// applySearch — the scope fix of §4.
//
// Both modes used `tableData`, which the search box does not touch: searching
// "ลิป" and pressing Export handed you all 120 rows. `searchedData` is where
// the query lands (clientPaginationMixin.js:98-105), and `queriedData` reads it
// like this — minus the .slice() that makes it a page.
//
// Same Fuse instance and options as the mixin (keys propsToSearch, threshold
// 0.1) so the file holds exactly the rows the table showed. fuse.js is pinned
// at ^3.2.0 and 3.x's search() returns the items themselves, not {item, score}
// wrappers — a Fuse 6 bump has to revisit this line and the mixin's together.
//
// An empty result for a non-empty query returns [], not everything: that is
// what the table does, and the caller turns it into "nothing to export" rather
// than a surprise 120-row file.
export const applySearch = (rows, query, keys = ["name", "tag"]) => {
	const list = Array.isArray(rows) ? rows : [];
	const q = query === null || query === undefined ? "" : String(query);
	if (q === "") return list;
	if (list.length === 0) return [];
	const fuse = new Fuse(list, { keys, threshold: 0.1 });
	return fuse.search(q);
};
